import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Factory,
  Search,
  Filter,
  ChevronRight,
  Globe,
  Ship,
  Users,
  Star,
  RefreshCw,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const PIPELINE_LABELS = {
  raw_imported: { label: "Raw Imported", color: "bg-slate-100 text-slate-700" },
  profile_enriched: { label: "Profile Enriched", color: "bg-blue-100 text-blue-700" },
  contact_identified: { label: "Contact Identified", color: "bg-cyan-100 text-cyan-700" },
  outreach_sent: { label: "Outreach Sent", color: "bg-yellow-100 text-yellow-700" },
  response_received: { label: "Response Received", color: "bg-orange-100 text-orange-700" },
  requirement_identified: { label: "Requirement Identified", color: "bg-purple-100 text-purple-700" },
  qualified_lead: { label: "Qualified Lead", color: "bg-green-100 text-green-700" },
  converted: { label: "Converted", color: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Lost", color: "bg-red-100 text-red-700" }
};

const ExportFactories = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [factories, setFactories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    country: searchParams.get('country') || "",
    pipeline_stage: searchParams.get('stage') || "",
    min_score: "",
    max_score: "",
    min_shipments: "",
    sort_by: "target_score",
    sort_order: "desc"
  });

  useEffect(() => {
    fetchFactories();
  }, [filters]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/export/dashboard`);
      if (response.data.by_country) {
        setCountries(response.data.by_country.map(c => c._id || c.country).filter(Boolean));
      }
    } catch (error) {
      console.error("Failed to fetch countries");
    }
  };

  const fetchFactories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.country) params.append('country', filters.country);
      if (filters.pipeline_stage) params.append('pipeline_stage', filters.pipeline_stage);
      if (filters.min_score) params.append('min_score', filters.min_score);
      if (filters.max_score) params.append('max_score', filters.max_score);
      if (filters.min_shipments) params.append('min_shipments', filters.min_shipments);
      params.append('sort_by', filters.sort_by);
      params.append('sort_order', filters.sort_order);
      params.append('limit', '100');

      const response = await axios.get(`${API}/export/factories?${params.toString()}`);
      setFactories(response.data.factories);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load factories");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      country: "",
      pipeline_stage: "",
      min_score: "",
      max_score: "",
      min_shipments: "",
      sort_by: "target_score",
      sort_order: "desc"
    });
  };

  const hasActiveFilters = filters.country || filters.pipeline_stage || filters.min_score || filters.max_score || filters.min_shipments;

  return (
    <div className="space-y-6" data-testid="export-factories-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Factories</h1>
          <p className="text-slate-500">{total} factories in your region</p>
        </div>
        <Button variant="outline" onClick={fetchFactories} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search factories..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                  data-testid="factory-search-input"
                />
              </div>
            </div>
            
            <Select
              value={filters.country || "all"}
              onValueChange={(value) => setFilters({ ...filters, country: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-40" data-testid="country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.pipeline_stage || "all"}
              onValueChange={(value) => setFilters({ ...filters, pipeline_stage: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-48" data-testid="stage-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {Object.entries(PIPELINE_LABELS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button 
              variant={showFilters ? "secondary" : "outline"} 
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              More Filters
              {hasActiveFilters && <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">!</Badge>}
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="gap-1 text-slate-500">
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Min Score</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.min_score}
                  onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
                  data-testid="min-score-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Max Score</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={filters.max_score}
                  onChange={(e) => setFilters({ ...filters, max_score: e.target.value })}
                  data-testid="max-score-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Min Shipments</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.min_shipments}
                  onChange={(e) => setFilters({ ...filters, min_shipments: e.target.value })}
                  data-testid="min-shipments-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort By</Label>
                <Select
                  value={filters.sort_by}
                  onValueChange={(value) => setFilters({ ...filters, sort_by: value })}
                >
                  <SelectTrigger data-testid="sort-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="target_score">Target Score</SelectItem>
                    <SelectItem value="shipment_count">Shipment Count</SelectItem>
                    <SelectItem value="supplier_count">Supplier Count</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factories Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : factories.length === 0 ? (
            <div className="text-center py-16">
              <Factory className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No factories found. Import shipment data to create factories.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">Score</TableHead>
                  <TableHead>Factory</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>HSN Codes</TableHead>
                  <TableHead>Shipments</TableHead>
                  <TableHead>Suppliers</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factories.map((factory) => (
                  <TableRow 
                    key={factory.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/export/factories/${factory.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-bold text-slate-900">{factory.target_score}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{factory.name}</p>
                        {factory.import_countries?.length > 0 && (
                          <p className="text-xs text-blue-600">Imports: {factory.import_countries.slice(0, 2).join(', ')}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-slate-400" />
                        <span className="text-sm">{factory.country}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        {factory.hs_codes?.length > 0 ? (
                          <span className="font-mono text-slate-700">
                            {factory.hs_codes.slice(0, 2).join(', ')}
                            {factory.hs_codes.length > 2 && <span className="text-slate-400"> +{factory.hs_codes.length - 2}</span>}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Ship className="w-3 h-3 text-slate-400" />
                        <span className="text-sm font-medium">{factory.shipment_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-slate-400" />
                        <span className="text-sm">{factory.supplier_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={PIPELINE_LABELS[factory.pipeline_stage]?.color}>
                        {PIPELINE_LABELS[factory.pipeline_stage]?.label || factory.pipeline_stage}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportFactories;
